import {
    RiAddFill,
    RiDeleteBinLine,
    RiDownload2Line,
    RiDownloadCloudLine,
    RiDownloadLine,
    RiFileCopyLine, RiMoreFill, RiRestartLine, RiSettings4Line,
    RiTableLine,
} from "@remixicon/react";
import React, {
    ReactElement, useEffect, useRef, useState,
} from "react";
import { createPortal } from "react-dom";
import {
    GetReportsDataDocument,
    MetricsDashboard,
    MetricsDashboardData,
    MetricsDashboardGraph,
    MetricsDashboardGraphDataSource, MetricsDashboardGraphInput,
    MetricsDashboardGraphType,
    MetricsInput, PermissionAction, useGetReportsDataQuery,
    useGetReportsListQuery,
    useSaveMetricDashboardMutation,
} from "../../__generated__/api";
import { Loading } from "../../components";

import { cloneWithoutTypename } from "../../api";
import { ReactComponent as EmptyReportLight } from "../../assets/img/empty-report-light.svg";
import { ReactComponent as EmptyReportDark } from "../../assets/img/empty-report-dark.svg";
import Confirm from "../../components/confirm/Confirm";
import useDeleteConfirm from "../../components/confirm/Delete";
import Button from "../../components/theme/button/Button";
import Dropdown from "../../components/theme/dropdown/Dropdown";
import TopPanel from "../../components/top-panel/TopPanel";
import useArticleContext from "../../contexts/HelpContext";
import { LoggingHandler } from "../../contexts/logging/LoggingContext";
import useRouting, { filterEmptyParameters, useParams } from "../../contexts/Routing";
import { Time } from "../../date";
import useAddWidgetTemplate from "./AddWidgetTemplate/AddWidgetTemplate";
import EditWidget from "./EditWidget/EditWidget";
import Table, { createRowsAndColumns } from "./graphs/Table";
import ReportDisplay, { MAX_COLUMNS, RowGroup } from "./ReportDisplay";
import Styles from "./Reports.module.scss";
import BoardToolbar, { createGraphId } from "./Toolbar/BoardToolbar";
import { ARTICLES } from "../../components/help/Help";
import { Themes, useTheme } from "../../contexts/Theme";
import NoResults from "../../components/agent-v3/board/no-results/NoResults";
import { useChangingHandler } from "../../use-changing-callback";

function getStartDate() {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date;
}
function deserializeParams(params: Record<string, unknown>): MetricsInput {
    return filterEmptyParameters({
        ...params,
        startDate: params.startDate ? new Date(params.startDate as string) : getStartDate(),
        endDate: params.endDate ? new Date(params.endDate as string) : undefined,
        formIds: (!params.formIds || Array.isArray(params.formIds)) ? params.formIds : [params.formIds],
        tags: (!params.tags || Array.isArray(params.tags)) ? params.tags : [params.tags],
        createdDate: undefined,
    } as MetricsInput);
}

const VIEW_EXCLUDE_FIELDS = new Set([
    "urn:fq:form-field:id",
]);

export default function ReportsV3(): ReactElement {
    useArticleContext(ARTICLES.REPORTS);

    const { getSearchParams, historyPushSearch, Paths } = useRouting();
    const theme = useTheme();

    const [shouldShowEdit, setShouldShowEdit] = useState(false);
    const [currentReport, setCurrentReport] = useState<MetricsDashboard>();

    const [viewData, setViewData] = useState<MetricsDashboardGraph>();

    const { reportId } = useParams(Paths.REPORTS_GET);

    const [saveReport] = useSaveMetricDashboardMutation();

    const [filters, setFilters] = useState<MetricsInput>(() => deserializeParams(getSearchParams(Paths.REPORTS_GET) ?? {
        startDate: getStartDate(),
    }));

    const [reports, setReports] = useState<MetricsDashboard[]>([]);
    const rootRef = useRef<HTMLDivElement>(null);

    const { data: dashboardListData, loading: isListLoading } = useGetReportsListQuery();

    const createInput = () => ({
        id: currentReport?.id,
        endDate: filters.endDate?.toISOString(),
        startDate: filters.startDate?.toISOString(),
        assigneeIds: filters.assigneeIds,
        conversationSourceIds: filters.conversationSourceIds,
        formIds: filters.formIds,
        priorities: filters.priorities,
        queueIds: filters.queueIds,
        requesterIds: filters.requesterIds,
    });
    const { data, loading: isLoading } = useGetReportsDataQuery({
        skip: !currentReport?.id,
        fetchPolicy: "network-only",
        variables: {
            input: createInput(),
        },
    });

    useEffect(() => {
        if (dashboardListData?.metricsDashboards) {
            setReports(dashboardListData?.metricsDashboards as MetricsDashboard[]);
        }
    }, [dashboardListData?.metricsDashboards]);

    useEffect(() => {
        const first = reports?.find(dash => dash.id === reportId)
            ?? reports?.firstElement;
        if (first) {
            setCurrentReport(first as MetricsDashboard);
        }
    }, [reports, reportId]);

    useEffect(() => {
        const params = Object.create(null);
        Object.entries({ ...filters }).forEach(([key, value]) => {
            if (value?.startDate || value?.endDate || value?.pre) {
                return;
            }
            if (value != null && value !== "" && (!Array.isArray(value) || value.isNotEmpty)) {
                params[key] = value instanceof Date ? value.toISOString() : value;
            }
        });

        historyPushSearch(params);
    }, [filters]);

    const [editGraph, setEditGraph] = useState<MetricsDashboardGraph>();

    const [graphs, setGraphs] = useState<MetricsDashboardGraph[]>([]);

    const saveGraphs = (current: MetricsDashboardGraph[]) => {
        LoggingHandler.instance.wrap(async () => {
            if (!currentReport) {
                return;
            }
            const reportIndex = reports.findIndex(row => row.id === currentReport.id);
            const copy = [...reports];
            const previousGraphs = copy[reportIndex].settings.graphs;
            copy[reportIndex] = {
                ...currentReport,
                settings: {
                    ...currentReport.settings,
                    graphs: current,
                },
            };
            setReports(copy);

            await saveReport({
                refetchQueries: [
                    previousGraphs.length < current.length
                        ? {
                            query: GetReportsDataDocument,
                            variables: { input: createInput() },
                        }
                        : undefined,
                ].filterNull(),
                variables: {
                    id: currentReport?.id,
                    input: {
                        name: currentReport?.name as string,
                        settings: {
                            graphs: current
                                .filterNull()
                                .map(cloneWithoutTypename) as MetricsDashboardGraphInput[],
                        },
                    },
                },
            });
        });
    };

    const isAdmin = data?.metricsDashboardWithData?.dashboard.currentUserPermissionAction === PermissionAction.Modify;
    const setAndSaveGraphs = (input: MetricsDashboardGraph[]) => {
        setGraphs(input);
        if (isAdmin) {
            saveGraphs(input);
        }
    };

    const addGraph = (graph: MetricsDashboardGraph) => {
        const graphCopy = { ...graph };
        graphCopy.id ??= createGraphId();
        graphCopy.width ??= 2;
        graphCopy.height ??= 2;
        const lastRow = graphs.maxValue(row => row.locationY) ?? 0;
        const lastElement = graphs
            .filter(item => item.locationY === lastRow)
            .max(undefined, item => item.locationX);
        if (!lastElement) {
            graphCopy.locationX ??= 0;
            graphCopy.locationY ??= 0;
        } else if (lastElement.locationX + lastElement.width + graphCopy.width < MAX_COLUMNS) {
            graphCopy.locationX ??= lastElement.locationX + lastElement.width;
            graphCopy.locationY ??= lastElement.locationY;
        } else {
            graphCopy.locationX ??= 0;
            graphCopy.locationY ??= lastElement.locationY + 1;
        }
        const graphsCopy = [...graphs];
        const index = graphsCopy.findIndex(row => row.id === graph.id);
        if (index >= 0) {
            graphsCopy[index] = graphCopy;
        } else {
            graphsCopy.push(graphCopy);
        }

        setAndSaveGraphs(graphsCopy);
        setShouldShowEdit(false);
        setEditGraph(undefined);

        setTimeout(() => {
            const element = rootRef.current?.querySelector(`#graph-${graphCopy.id.replace(/[^0-9a-zA-Z]/g, "")}`);
            if (element) {
                element.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                    inline: "center",
                });
                element.classList.add("focus-pulse");
                setTimeout(() => {
                    element.classList.remove("focus-pulse");
                }, 1000);
            }
        }, 500);
    };

    const { element: editWidget, setShouldShow: showAddWidget } = useAddWidgetTemplate({
        onCreate: () => {
            setShouldShowEdit(true);
        },
        onSelected: graph => {
            addGraph({
                ...graph,
                id: createGraphId(),
            });
        },
    });

    useEffect(() => {
        if (!data?.metricsDashboardWithData) {
            return;
        }
        setGraphs(data.metricsDashboardWithData.dashboard.settings.graphs.map(row => ({ ...row })));
    }, [data?.metricsDashboardWithData]);

    const rowGroups = data?.metricsDashboardWithData?.data.toIdMap("dataSource", rowData => {
        const columnIndexMap = rowData?.columns.map((row, index) => ({ id: row.id, index })).toIdMap("id", "index") ?? {};
        const labelMap = rowData?.labels.toIdMap("id", "label") ?? {};
        return {
            rows: rowData,
            columnIndexMap,
            labelMap,
        };
    }) ?? {};

    const deleteGraph = useDeleteConfirm<MetricsDashboardGraph>(graph => {
        const removed = graphs?.filter(row => row.id !== graph.id);
        setAndSaveGraphs(removed);
    }, item => item.title);

    const duplicate = (graph: MetricsDashboardGraph) => {
        const copy = [...graphs, { ...graph, id: createGraphId() }];
        setAndSaveGraphs(copy);
    };

    const displayDataTable = (graph: MetricsDashboardGraph) => {
        const tableGraph: MetricsDashboardGraph = {
            ...graph,
            type: MetricsDashboardGraphType.Table,
            dimensions: [
                {
                    fieldId: "urn:fq:form-field:ticket-number",
                    label: "Ticket Number",
                },
                ...(graph.dimensions ?? []),
                ...(graph.metrics ?? []).map(metric => ({
                    fieldId: metric.fieldId,
                    label: metric.label,
                    display: metric.display,
                })),
            ].dedupe(row => row.fieldId).filter(row => !VIEW_EXCLUDE_FIELDS.has(row.fieldId)),
            metrics: undefined,
        };

        setViewData(tableGraph);
    };

    const downloadCsv = useChangingHandler((graph: MetricsDashboardGraph) => {
        const source = rowGroups[graph.dataSource];
        if (!source) {
            return;
        }

        const { columns, rowsData } = createRowsAndColumns({
            graph,
            data: source?.rows as MetricsDashboardData,
            columnIndexMap: source?.columnIndexMap,
            labelMap: source?.labelMap,
        });
        const headers = columns.map(column => JSON.stringify(column)).join(",");
        const csv = rowsData.map(row => {
            const rowData = row.map(value => JSON.stringify(value.textDisplay));
            return rowData.join(",");
        }).join("\n");

        const blob = new Blob([`${headers}\n${csv}`], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        window.location.href = url;
    });

    let control;
    if (isAdmin && graphs.isEmpty) {
        control = (
            <div className={Styles["empty-report"]}>
                {theme.getTheme() === Themes.LIGHT ? <EmptyReportLight /> : <EmptyReportDark />}
                <h4>
                    Add Widgets to your Custom Report
                </h4>
                <p>Add widgets to customize your report with dynamic visuals and key data insights</p>
                <Button prefixIcon={<RiAddFill />} size="medium" onClick={() => showAddWidget(true)}>
                    Add Widget
                </Button>
            </div>
        );
    } else if (!rowGroups[MetricsDashboardGraphDataSource.Tickets]?.rows?.rows.isNotEmpty) {
        control = (
            <NoResults
                title="No Report Data"
                className={Styles["no-results"]}
                text="Try adjusting your filters to find more data."
                resetButtonText="Reset Filters"
                onClear={() => {
                    setFilters({
                        startDate: new Date(filters.startDate.getTime() - Time.WEEK),
                    });
                }}
                resetIcon={<RiRestartLine />}
            />
        );
    } else {
        control = (
            <ReportDisplay
                key={data?.metricsDashboardWithData?.dashboard.id}
                graphs={graphs}
                isAdmin={isAdmin}
                // eslint-disable-next-line react/no-unstable-nested-components
                createMenu={graph => (
                    <>
                        {isAdmin && (
                            <Button
                                variant="none"
                                size="small"
                                prefixIcon={<RiSettings4Line />}
                                onClick={() => {
                                    setEditGraph(graph);
                                    setShouldShowEdit(true);
                                }}
                            />
                        )}

                        <Dropdown>
                            <Dropdown.Toggle
                                size="small"
                                variant="none"
                                prefixIcon={<RiMoreFill />}
                            />
                            {createPortal(
                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        prefixIcon={<RiTableLine />}
                                        onClick={() => displayDataTable(graph)}
                                    >
                                        View Data
                                    </Dropdown.Item>
                                    {isAdmin && (
                                        <>
                                            <Dropdown.Item
                                                prefixIcon={<RiFileCopyLine />}
                                                onClick={() => duplicate(graph)}
                                            >
                                                Duplicate
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                                prefixIcon={<RiDeleteBinLine />}
                                                onClick={() => deleteGraph.setDeleting(graph)}
                                            >
                                                Delete
                                            </Dropdown.Item>
                                        </>
                                    )}
                                    <Dropdown.Item
                                        prefixIcon={<RiDownloadCloudLine />}
                                        onClick={() => downloadCsv(graph)}
                                    >
                                        Export
                                    </Dropdown.Item>
                                </Dropdown.Menu>,
                                document.body,
                            )}

                        </Dropdown>

                    </>
                )}
                rowGroups={rowGroups as Record<string, RowGroup>}
                onChange={items => setAndSaveGraphs(items)}
            />
        );
    }
    const viewDataDataSource = viewData ? rowGroups[viewData.dataSource] : undefined;

    return (

        <div className="flex-grow-1 d-flex flex-column h-100">
            {deleteGraph.element}
            {editWidget}
            <TopPanel title="Reports" />
            <BoardToolbar
                filters={filters}
                dashboards={reports as MetricsDashboard[]}
                setFilters={value => {
                    setFilters({
                        ...value,
                        startDate: value.createdDate?.startDate,
                        endDate: value.createdDate?.endDate,
                    });
                }}
                onAddWidget={isAdmin ? () => showAddWidget(true) : undefined}
                selectedReport={currentReport}
            />
            <EditWidget
                data={data?.metricsDashboardWithData?.data as MetricsDashboardData[]}
                shouldShow={shouldShowEdit}
                onClose={() => {
                    setEditGraph(undefined);
                    setShouldShowEdit(false);
                }}
                value={editGraph}
                onSave={addGraph}
            />

            <Confirm
                title="Data Table"
                onHide={() => setViewData(undefined)}
                bodyClassName={Styles.modal}
                className={Styles["modal-dialog"]}
                shouldShow={viewData != null}
                size="xl"
            >
                {viewData && viewDataDataSource && (
                    <Table
                        graph={viewData}
                        data={viewDataDataSource.rows as MetricsDashboardData}
                        columnIndexMap={viewDataDataSource.columnIndexMap}
                        labelMap={viewDataDataSource.labelMap}
                    />
                )}
            </Confirm>

            {(isLoading || isListLoading) ? (
                <Loading />
            ) : (
                <div className={Styles["grid-container"]} ref={rootRef}>
                    {control}
                </div>
            )}

        </div>

    );
}

ReportsV3.defaultProps = {
    children: undefined,
    title: undefined,
};
